import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Card, EmptyState, Skeleton } from '@/ui/primitives'

type Utilizator = {
  id: number; username: string; email: string | null; full_name: string | null
  is_active: boolean; is_admin: boolean; created_at: string | null
  last_login: string | null; roles: { id: number; name: string }[] | null
}
type Rol = { id: number; name: string; description: string | null; user_count: number
  permissions: unknown[] | null }

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' }) : 'niciodata'

export function Users() {
  const u = useQuery({ queryKey: ['utilizatori'], queryFn: () => api.get<Utilizator[]>('/users'), retry: false })
  const r = useQuery({ queryKey: ['roluri'], queryFn: () => api.get<Rol[]>('/users/roles/all'), retry: false })

  /** Endpointul raspunde 403 pentru conturile fara drept de administrare — ecranul spune
   *  asta pe sleau, in loc sa arate o lista goala. */
  const faraDrept = (u.error as ApiError | null)?.status === 403

  return (
    <>
      <PageHeader title="Utilizatori si roluri" count={u.data ? `${u.data.length} conturi` : undefined} />
      <div className="px-8 py-6 space-y-6">
        {faraDrept ? (
          <Card><EmptyState
            title="Nu ai drepturi pentru aceasta sectiune"
            hint="Administrarea conturilor e disponibila doar administratorilor." /></Card>
        ) : (
          <>
            <Card className="overflow-hidden">
              {u.isPending ? <Skeleton rows={4} /> : (
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="text-left text-[11.5px] uppercase tracking-[0.1em] text-muted">
                      <th className="font-medium px-5 py-3">Cont</th>
                      <th className="font-medium px-5 py-3">Roluri</th>
                      <th className="font-medium px-5 py-3">Ultima autentificare</th>
                      <th className="font-medium px-5 py-3">Stare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(u.data ?? []).map((x) => (
                      <tr key={x.id} className="border-t border-line">
                        <td className="px-5 py-3">
                          <span className="font-medium">{x.full_name || x.username}</span>
                          <span className="block text-[12.5px] text-muted">
                            {x.username}{x.email ? ` · ${x.email}` : ''}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-[13px]">
                          {x.is_admin && <Badge tone="warn">administrator</Badge>}
                          {(x.roles ?? []).map((rol) => (
                            <span key={rol.id} className="ml-1 text-muted">{rol.name}</span>
                          ))}
                        </td>
                        <td className="px-5 py-3 text-[13px] text-muted tnum">{zi(x.last_login)}</td>
                        <td className="px-5 py-3">
                          {x.is_active ? <Badge tone="good">activ</Badge> : <Badge tone="neutral">inactiv</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            <div>
              <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-2">Roluri</h2>
              <Card className="overflow-hidden">
                {r.isPending ? <Skeleton rows={3} /> : (
                  <table className="w-full text-[14px]">
                    <tbody>
                      {(r.data ?? []).map((rol) => (
                        <tr key={rol.id} className="border-t border-line first:border-0">
                          <td className="px-5 py-3 font-medium w-56">{rol.name}</td>
                          <td className="px-5 py-3 text-[13.5px] text-muted">{rol.description ?? '—'}</td>
                          <td className="px-5 py-3 text-right text-[13px] text-muted tnum">
                            {rol.user_count} {rol.user_count === 1 ? 'cont' : 'conturi'}
                            {rol.permissions && <span className="ml-3">{rol.permissions.length} permisiuni</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </>
  )
}
